'use client';

import { useQuery } from '@tanstack/react-query';
import { useLocale } from 'next-intl';
import { useState, useEffect, useCallback } from 'react';
import { useFilterStore } from '../filters/filter-store';
import { useSearch } from '../search/use-search';
import type { Database } from '@medizzconnect/db';

type DoctorProfileRow = Database['public']['Tables']['doctor_profiles']['Row'];

export type VideoCardData = Pick<
  DoctorProfileRow,
  | 'id'
  | 'first_name'
  | 'last_name'
  | 'title'
  | 'berufsbezeichnung'
  | 'city'
  | 'video_url'
  | 'thumbnail_url'
  | 'view_count'
  | 'languages'
  | 'insurance_types'
  | 'is_clinic_member'
> & {
  plan: string;
  is_mock?: boolean;
  /** Optimierte Video-URL aus der Processing-Pipeline (optional, rückwärtskompatibel) */
  processed_video_url?: string | null;
};

export interface VideosPage {
  profiles: VideoCardData[];
  total: number;
  page: number;
  perPage: number;
  mockClickEnabled?: boolean;
}

const DEFAULT_PER_PAGE = 25; // 5×5 Grid
const FILTERED_PER_PAGE = 25; // Bei aktiven Filtern
const MAX_PER_PAGE = 25;

function createShuffleSeed(): number {
  return Date.now() + Math.floor(Math.random() * 1_000_000);
}

async function fetchVideos(params: {
  page: number;
  perPage?: number;
  categoryId?: string | null;
  subcategoryName?: string | null;
  treatmentName?: string | null;
  query?: string;
  locale?: string;
  rand?: number;
}): Promise<VideosPage> {
  const searchParams = new URLSearchParams();
  searchParams.set('page', String(params.page));
  searchParams.set('perPage', String(params.perPage ?? DEFAULT_PER_PAGE));
  if (params.categoryId) searchParams.set('categoryId', params.categoryId);
  if (params.subcategoryName) searchParams.set('subcategoryName', params.subcategoryName);
  if (params.treatmentName) searchParams.set('treatmentName', params.treatmentName);
  if (params.query) searchParams.set('q', params.query);
  if (params.locale) searchParams.set('locale', params.locale);
  if (params.rand) searchParams.set('rand', String(params.rand));

  const res = await fetch(`/api/videos?${searchParams.toString()}`);
  if (!res.ok) throw new Error('Failed to fetch videos');
  return res.json() as Promise<VideosPage>;
}

/**
 * Paginated-Query-Hook für Videos.
 * Reagiert auf Filter-Änderungen → setzt Seite auf 1 zurück.
 * SSR-Daten werden als initialData für Seite 1 ohne Filter verwendet.
 */
export function useVideos(initialData?: VideosPage, perPage?: number) {
  const { categoryId, subcategoryName, treatmentName } = useFilterStore();
  const { deferredQuery } = useSearch();
  const locale = useLocale();
  const [currentPage, setCurrentPage] = useState(1);
  const [shuffleSeed, setShuffleSeed] = useState<number>(() => createShuffleSeed());

  // Seiten-Navigation → jede Bewegung mischt die Reihenfolge neu,
  // damit Besucher nie eine klar feste Gesamtliste erkennen können.
  const navigatePage = useCallback((page: number) => {
    setCurrentPage(page);
    setShuffleSeed(createShuffleSeed());
  }, []);

  // Filter-Wechsel → zurück auf Seite 1, neues Shuffle
  useEffect(() => {
    setCurrentPage(1);
    setShuffleSeed(createShuffleSeed());
  }, [categoryId, deferredQuery, subcategoryName, treatmentName]);

  const hasActiveFilters = !!(categoryId || deferredQuery || subcategoryName || treatmentName);

  // Immer 25 pro Seite (5×5 Grid)
  const effectivePerPage = Math.min(
    perPage ?? DEFAULT_PER_PAGE,
    MAX_PER_PAGE,
  );

  const queryKey = [
    'home-videos',
    categoryId,
    deferredQuery,
    subcategoryName,
    treatmentName,
    effectivePerPage,
    currentPage,
    shuffleSeed,
  ] as const;

  const result = useQuery<VideosPage>({
    queryKey,
    queryFn: () =>
      fetchVideos({
        page: currentPage,
        perPage: effectivePerPage,
        categoryId,
        subcategoryName: subcategoryName || undefined,
        treatmentName: treatmentName || undefined,
        query: deferredQuery || undefined,
        locale,
        rand: shuffleSeed,
      }),
    staleTime: 30 * 1000, // 30 Sekunden – verhindert Sofort-Refetch nach Hydration (CLS!)
    gcTime: 5 * 60_000,
    // SSR-Daten nur für Seite 1 ohne Filter
    initialData:
      !hasActiveFilters && currentPage === 1 && initialData
        ? initialData
        : undefined,
    placeholderData: (prev) => prev,
  });

  const totalPages = result.data
    ? Math.max(
        result.data.profiles.length > 0 ? 1 : 0,
        Math.ceil(result.data.total / effectivePerPage),
      )
    : 0;
  const mockClickEnabled = result.data?.mockClickEnabled ?? false;

  return {
    ...result,
    profiles: result.data?.profiles ?? [],
    mockClickEnabled,
    currentPage,
    setCurrentPage,
    navigatePage,
    totalPages,
    queryKey,
  };
}
